"use client";

import { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Phone, Mail, MapPin, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative font-default font-bold text-foreground hover:text-primary transition-colors py-2 text-sm tracking-widest uppercase">
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 shadow-sm shadow-primary/50" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-32 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-8">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="text-2xl font-default font-bold text-foreground hover:text-primary transition-colors uppercase tracking-wider">
          {item.label}
        </Link>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="265139" id="header" className="bg-background text-foreground w-full flex flex-col">
      {}
      <div className="hidden md:flex h-10 bg-secondary items-center justify-between px-4 lg:px-8 text-xs font-mono border-b border-primary/20">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="border-primary text-primary bg-primary/10 rounded-md px-2 py-0.5 font-bold tracking-widest uppercase">
            Live Tracking
          </Badge>
          <span className="text-secondary-foreground/70 hidden lg:inline-block uppercase tracking-tight">
            Austin's Premier Street Taco Experience
          </span>
        </div>
        <div className="flex items-center gap-6 text-secondary-foreground/80">
          <Link to="tel:555-ROLL-NOW" className="flex items-center gap-2 hover:text-primary transition-colors group text-foreground">
            <Phone className="size-3.5 text-primary group-hover:scale-110 transition-transform" />
            <span>555-ROLL-NOW</span>
          </Link>
          <Link to="mailto:hello@rollingtacos.com" className="flex items-center gap-2 hover:text-primary transition-colors group text-foreground">
            <Mail className="size-3.5 text-primary group-hover:scale-110 transition-transform" />
            <span>hello@rollingtacos.com</span>
          </Link>
          <div className="flex items-center gap-2">
            <MapPin className="size-3.5 text-primary" />
            <span>Downtown Austin</span>
          </div>
        </div>
      </div>

      {}
      <header className={`sticky top-0 z-50 h-20 transition-all duration-300 ${isScrolled ? "bg-background/70 backdrop-blur-xl border-b border-primary/30 shadow-lg shadow-primary/10" : "bg-background border-b border-transparent"}`}>
        <div className="container mx-auto px-4 lg:px-8 h-full flex items-center justify-between">
          {}
          <Link to="/" className="flex-shrink-0 z-10">
            <WvcLogo className="h-10 w-auto drop-shadow-[0_0_8px_rgba(249,115,22,0.6)]" />
          </Link>

          {}
          <div className="absolute left-1/2 -translate-x-1/2 hidden lg:block">
            <MenuProvider menu_id="16">
              <DesktopMenu />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-4 z-10">
            <Button className="hidden md:flex rounded-md border-2 border-primary bg-primary text-primary-foreground hover:bg-transparent hover:text-primary shadow-md shadow-primary/20 relative overflow-hidden group">
              <span className="relative z-10 font-default font-bold tracking-widest uppercase">Find Us</span>
            </Button>

            {}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:bg-primary/20 hover:text-primary">
                  <Menu className="size-6" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-l border-primary/30 w-[300px] sm:w-[400px]">
                <div className="flex flex-col h-full pt-6">
                  <WvcLogo className="h-10 w-auto mb-8 drop-shadow-[0_0_8px_rgba(249,115,22,0.6)]" />
                  
                  <MenuProvider menu_id="16">
                    <MobileMenu />
                  </MenuProvider>

                  <div className="mt-auto pb-8 flex flex-col gap-6">
                    <div className="flex flex-col gap-4 text-sm font-mono text-muted-foreground">
                      <Link to="tel:555-ROLL-NOW" className="flex items-center gap-3 hover:text-primary transition-colors text-foreground">
                        <Phone className="size-4 text-primary" />
                        <span>555-ROLL-NOW</span>
                      </Link>
                      <Link to="mailto:hello@rollingtacos.com" className="flex items-center gap-3 hover:text-primary transition-colors text-foreground">
                        <Mail className="size-4 text-primary" />
                        <span>hello@rollingtacos.com</span>
                      </Link>
                    </div>
                    <Button className="w-full rounded-md border-2 border-primary bg-primary text-primary-foreground hover:bg-transparent hover:text-primary shadow-md shadow-primary/20 relative overflow-hidden">
                      <span className="relative z-10 font-default font-bold tracking-widest uppercase">Find Us</span>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </section>;
}